import { useState } from 'react';

/**
 * Custom hook för att hantera kundens svar på offert (godkänna/neka/kommentera)
 * 
 * @param {Object} offert - Offert-objektet
 * @param {Function} setOffert - Funktion för att uppdatera offert
 * @param {Function} fetchFeedbackThread - Funktion för att ladda om feedback-tråden
 * @param {Function} fetchIntelligentStatus - Funktion för att ladda om intelligent status
 * @param {Function} onSuccess - Callback för att visa success modal
 * @param {Function} onError - Callback för att visa error modal
 * @returns {Object} - State och funktioner för customer response-hantering
 */
const useOffertCustomerResponse = (offert, setOffert, fetchFeedbackThread, fetchIntelligentStatus, onSuccess, onError, isNewerVersion = false) => {
  // State för kundens svar
  const [response, setResponse] = useState(''); // 'accepted', 'declined', 'commented'
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Kontrollera om kunden kan svara på offerten
  // Kunden kan INTE svara på offerten (godkänn/neka/kommentera) om:
  // 1. De har godkänt eller nekat offerten
  const hasFinalResponse = offert && (
    ['accepted', 'declined'].includes(offert.status) ||
    ['accepted', 'declined'].includes(offert.customerResponse?.action)
  );
  
  const canRespond = offert && !hasFinalResponse && offert.status !== 'draft';
  
  // Visa status-meddelande endast för slutgiltiga svar (accepted/declined)
  const hasResponded = hasFinalResponse;

  // Skicka kundens svar
  const handleSubmitResponse = async () => {
    if (!response || !offert?._id) {
      if (onError) {
        onError({
          title: 'Felaktig input',
          message: 'Vänligen välj ett svar (Godkänn, Neka eller Kommentera).',
          type: 'error'
        });
      }
      return;
    }

    // Validera kommentar för "commented" och "declined"
    if ((response === 'commented' || response === 'declined') && !comment.trim()) {
      if (onError) {
        onError({
          title: 'Kommentar krävs',
          message: response === 'declined' ? 
            'Vänligen ange en kommentar om varför du nekar offerten.' :
            'Vänligen ange din kommentar.',
          type: 'error'
        });
      }
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/customer-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          response,
          comment: comment.trim()
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Kunde inte skicka svar');
      }

      const data = await res.json();

      if (data.success && data.data) {
        // Uppdatera offert-state
        setOffert(data.data);

        // Rensa formulär
        setResponse('');
        setComment('');

        // Ladda om feedback-tråd och status för att visa uppdaterad data
        if (fetchFeedbackThread) {
          await fetchFeedbackThread();
        }
        if (fetchIntelligentStatus) {
          await fetchIntelligentStatus();
        }

        // Visa framgångsmeddelande
        if (onSuccess) {
          /**
           * Ett TILLÄGG får egna meddelanden.
           *
           * `isNewerVersion` bygger på version inom spåret, så en tilläggsoffert
           * på v1.0 hamnade i "första gången"-grenen och sa "Byggare bokad!" —
           * till en kund som varit i produktion i månader och redan har en
           * bokad byggare.
           */
          const tillagg = offert?.extraNummer != null;
          const tillaggsNamn = tillagg ? `Tilläggsoffert ${offert.extraNummer}` : null;

          const messages = tillagg ? {
            accepted: {
              title: `${tillaggsNamn} godkänd!`,
              message: 'Byggkonsulten har informerats och lägger in det tillkommande arbetet i projektet. Din ursprungliga offert gäller fortsatt.'
            },
            declined: {
              title: `${tillaggsNamn} nekad`,
              message: 'Din respons har skickats till byggkonsulten. Det pågående projektet fortsätter som planerat.'
            },
            commented: {
              title: 'Kommentar skickad!',
              message: 'Din kommentar har skickats till byggkonsulten. De kommer att granska din feedback och återkomma.'
            }
          } : {
            accepted: isNewerVersion ? {
              title: 'Offert godkänd!',
              message: 'Byggkonsulten har informerats om att du godkänt den uppdaterade offerten.'
            } : {
              title: 'Byggare bokad!',
              message: 'Byggkonsulten har informerats och kommer att kontakta dig för nästa steg.'
            },
            declined: {
              title: 'Offert nekad',
              message: 'Din respons har skickats till byggkonsulten. De kan komma att kontakta dig för att diskutera alternativ.'
            },
            commented: {
              title: 'Kommentar skickad!',
              message: 'Din kommentar har skickats till byggkonsulten. De kommer att granska din feedback och återkomma.'
            }
          };

          onSuccess({
            ...messages[response],
            type: 'success'
          });
        }
      } else {
        throw new Error(data.message || 'Kunde inte skicka svar');
      }
    } catch (err) {
      console.error('❌ Fel vid skickande av kundsvar:', err);
      if (onError) {
        onError({
          title: 'Fel uppstod',
          message: err.message || 'Kunde inte skicka ditt svar. Försök igen.',
          type: 'error'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    // State
    response,
    comment,
    submitting,
    canRespond,
    hasResponded,

    // Setters
    setResponse,
    setComment,

    // Actions
    handleSubmitResponse,
  };
};

export default useOffertCustomerResponse;

